import { Link } from "react-router-dom";
import { ArrowRight, Brain, Users, Star, Shield, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/ui/Logo";
import { useEffect, useState } from "react";
import { loadTextContent } from "@/lib/textContent";
import type { TextContent } from "@/types/textContent";
import NewsletterSignup from "@/components/ui/NewsletterSignup";

const Index = () => {
  console.log('Index component rendering');
  const [text, setText] = useState<TextContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('Index useEffect running');
    let isMounted = true;

    const loadText = async () => {
      try {
        console.log('Starting to load text content...');
        setIsLoading(true);
        setError(null);
        const content = await loadTextContent("index");
        console.log('Loaded text content:', content);
        if (isMounted) {
          setText(content);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error loading text content:', err);
        if (isMounted) {
          setError('Failed to load page content');
          setIsLoading(false);
        }
      }
    };

    loadText();
    return () => {
      isMounted = false;
    };
  }, []);

  console.log('Current render state:', { isLoading, error, hasText: !!text });

  // Show loading state
  if (isLoading) {
    console.log('Rendering loading state...');
    // Corrected loading state JSX
    return (
      <div className="flex flex-1 items-center justify-center"> {/* Ensure flex-1 is applied */}
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-minnestig-purple mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    console.log('Rendering error state:', error);
    // Corrected error state JSX
    return (
      <div className="flex flex-1 items-center justify-center"> {/* Ensure flex-1 is applied */}
        <div className="text-center">
          <p className="text-red-600">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              className="mt-4"
            >
              Try Again
            </Button>
          </div>
        </div>
      // Removed extra closing div
    );
  }

  // Show content only when text is available
  if (!text) {
    console.log('No text content available');
    // Corrected no content state JSX
    return (
      <div className="flex flex-1 items-center justify-center"> {/* Ensure flex-1 is applied */}
        <div className="text-center">
          <p className="text-gray-600">No content available</p>
        </div>
      </div>
    );
  }

  console.log('Rendering main content with text:', text);
  console.log('MinneStig section content:', text?.minnestig);
  // Removed outer div and Navbar, handled by layout
  return (
    <>
      {/* Hero Section - Adjusted top padding since Navbar is fixed */}
      <section className="pt-8 pb-16 md:pt-16 md:pb-24 px-4"> {/* Reduced top padding */}
        <div className="container mx-auto flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 md:pr-10 space-y-6 animate-slideInLeft">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="text-minnestig-purple font-poppins">Demenshjältar</span>
              <span className="text-minnestig-teal"> tillsammans för en enklare vardag</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-lg">
              {text?.hero?.description}
            </p>
          </div>
          <div className="md:w-1/2 mt-12 md:mt-0 animate-slideInRight relative">
            <div className="relative overflow-hidden rounded-2xl shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-r from-minnestig-purple/20 to-minnestig-purple/5 z-10 rounded-2xl"></div>
              <img 
                src="/images/header-family.webp" 
                alt="Diverse group of older adults supporting each other" 
                className="w-full h-auto rounded-2xl object-cover"
                style={{ aspectRatio: "4/3" }}
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 h-24 w-24 bg-minnestig-lavender rounded-full -z-10"></div>
            <div className="absolute -top-6 -right-6 h-16 w-16 bg-minnestig-peach rounded-full -z-10"></div>
          </div>
        </div>
      </section>

      {/* About Demenshjältar Section */}
      <section className="py-16 bg-[#F8F2FF]"> {/* Changed background to match Kampanjer page */}
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-minnestig-teal mb-4">
              {text?.about?.title}
            </h2>
            <p className="text-lg text-gray-600">
              {text?.about?.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Value 1 */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="mb-4 rounded-full bg-minnestig-lavender/30 w-12 h-12 flex items-center justify-center">
                <Brain className="text-minnestig-purple" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-2">{text?.about?.values?.knowledge?.title}</h3>
              <p className="text-gray-600">
                {text?.about?.values?.knowledge?.description}
              </p>
            </div>
            
            {/* Value 2 */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="mb-4 rounded-full bg-minnestig-peach/30 w-12 h-12 flex items-center justify-center">
                <Shield className="text-minnestig-purple" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-2">{text?.about?.values?.support?.title}</h3>
              <p className="text-gray-600">
                {text?.about?.values?.support?.description}
              </p>
            </div>
            
            {/* Value 3 */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="mb-4 rounded-full bg-minnestig-lavender/30 w-12 h-12 flex items-center justify-center">
                <Users className="text-minnestig-purple" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-2">{text?.about?.values?.community?.title}</h3>
              <p className="text-gray-600">
                {text?.about?.values?.community?.description}
              </p>
            </div>
            
            {/* Value 4 */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="mb-4 rounded-full bg-minnestig-peach/30 w-12 h-12 flex items-center justify-center">
                <Star className="text-minnestig-purple" size={24} />
              </div>
              <h3 className="text-xl font-semibold text-minnestig-teal mb-2">{text?.about?.values?.quality?.title}</h3>
              <p className="text-gray-600">
                {text?.about?.values?.quality?.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MinneStig Product Showcase */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-minnestig-teal to-minnestig-darkpurple rounded-2xl p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {/* Added flex classes for vertical centering */}
              <div className="relative z-10 md:col-span-6 flex flex-col justify-center"> 
                {/* Added font-poppins */}
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-minnestig-purple mb-6 font-poppins">{text?.minnestig?.title}</h1> 
                <h2 className="text-2xl md:text-3xl font-medium mb-4 text-white/90">{text?.minnestig?.subtitle}</h2>
                <p className="text-white/80 text-lg mb-8">
                  {text?.minnestig?.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Changed Link to anchor tag pointing to #newsletter */}
                  <a href="#newsletter"> 
                    <Button size="lg" className="bg-minnestig-purple text-white border-minnestig-purple hover:bg-minnestig-darkpurple hover:border-minnestig-darkpurple w-full sm:w-auto">
                      Bli del av Väntelista
                    </Button>
                  </a>
                  {/* Removed "Läs mer om MinneStig" button */}
                </div>
              </div>

              <div className="md:col-span-6">
                <div className="flex justify-center items-center h-full">
                  <img 
                    src="/images/minnestig/mobile-checklist-preview-with-frame.png"
                    alt="MinneStig produktvisning"
                    className="w-full max-w-[14rem] h-auto object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-minnestig-lightgray">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-minnestig-teal mb-12 text-center">
            {text?.testimonials?.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {text?.testimonials?.items?.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-minnestig-purple/10 flex items-center justify-center mr-4">
                    <Quote className="text-minnestig-purple" size={24} />
                  </div>
                  <div>
                    <p className="text-minnestig-teal font-bold">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  {testimonial.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <NewsletterSignup />
      {/* Footer is handled by PublicLayout */}
    </>
  );
};

export default Index;
